import type {
  RentalDistributionFilters,
  RentalPayoutFilters,
} from '../types/rentalDistributionTypes'

export const rentalDistributionKeys = {
  all: ['rentalDistributions'] as const,

  lists: () => [...rentalDistributionKeys.all, 'list'] as const,
  list: (filters: RentalDistributionFilters) =>
    [...rentalDistributionKeys.lists(), filters] as const,

  details: () => [...rentalDistributionKeys.all, 'detail'] as const,
  detail: (distributionId: string) =>
    [...rentalDistributionKeys.details(), distributionId] as const,

  // Payouts hang off the distribution they belong to, so `detail(id)` is a
  // PREFIX of them: invalidating a distribution refreshes every payout filter
  // combination of it, and nothing belonging to any other distribution.
  payoutLists: (distributionId: string) =>
    [...rentalDistributionKeys.detail(distributionId), 'payouts'] as const,
  payouts: (distributionId: string, filters: RentalPayoutFilters) =>
    [...rentalDistributionKeys.payoutLists(distributionId), filters] as const,

  /**
   * Whether the signed-in admin may approve this run. Kept OUTSIDE detail(id)
   * on purpose: the answer depends on who is asking, so it must not ride on a
   * key another admin would read.
   */
  approvalPermission: (distributionId: string) =>
    [...rentalDistributionKeys.all, 'approvalPermission', distributionId] as const,

  /** Same "depends on who's asking" reasoning as approvalPermission, for payment. */
  paymentPermission: (distributionId: string) =>
    [...rentalDistributionKeys.all, 'paymentPermission', distributionId] as const,

  /** Same "depends on who's asking" reasoning as approvalPermission/paymentPermission, for reversal. */
  reversalPermission: (distributionId: string) =>
    [...rentalDistributionKeys.all, 'reversalPermission', distributionId] as const,

  /** KPI strip + sidebar badge. */
  counts: () => [...rentalDistributionKeys.all, 'counts'] as const,

  /**
   * Distinct 'YYYY-MM' periods that have at least one distribution — feeds the
   * month selector, so a period stays selectable even when the current filter
   * returns zero rows.
   */
  periods: () => [...rentalDistributionKeys.all, 'periods'] as const,

  /** Properties that can still be declared for a given month. */
  eligibleProperties: (periodKey: string) =>
    [...rentalDistributionKeys.all, 'eligibleProperties', periodKey] as const,
}
